import React, {
  useEffect,
  useState,
  memo,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react'
import {
  Paper,
  TableContainer,
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
  TableCell,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Tooltip,
  CircularProgress,
  Stack,
} from '@mui/material'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { makeStyles } from '@mui/styles'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import SyncIcon from '@mui/icons-material/Sync'
import GetAppIcon from '@mui/icons-material/GetApp'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import DescriptionIcon from '@mui/icons-material/Description'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import moment from 'moment'
import { useSelector } from 'react-redux'
import InfoIcon from '@mui/icons-material/Info'
import stylesheet from '../detail/detail-container.stylesheet'
import ConfirmationDialogComponent from '../detail/common/confirmation-dialog.component'
import { RootState, useAppThunkDispatch } from '../../../redux/store'
import { PatientService } from '../../../services/patient.service'
import Helper from '../../../utils/helper'
import {
  setSelectedStage,
  setClinicVisitsTable,
} from '../../../redux/common-data-slice'
import ColorBubbleComponent from '../detail/common/color-bubble.component'
import AddPatientComponent from '../detail/info-header/add-patient.component'
import { checkCondition } from '../detail/common/condition-check'
import helper from '../../../utils/helper'
import ExtractPatientComponent from '../detail/info-header/extract-patient.component'
import { environment } from '../../../environments/environment'
import { tableHeadeClinicVisit } from './patient-header-config'
import TestStatusDialogComponent from '../detail/common/test-status-dialog.component'

interface IProps {
  clinicVisitsTable: any
  // classes: ClassNameMap
}

const ClinicVisitTableComponent: React.FC<IProps> = ({ clinicVisitsTable }) => {
  const tableEl = useRef<any>()
  const url = useLocation()
  const history = useHistory()
  const classes = makeStyles(stylesheet)()
  const patientService = new PatientService()

  const [loading, setLoading] = useState(false)
  const [isApply, SetIsApply] = useState(true)
  const [programChips, setProgramChips] = useState([]) as any
  const [lastFetchParams, setLastFetchParams] = useState<string>('')
  const [dataInitialized, setDataInitialized] = useState(false)
  const [isPagination, setIsPagination] = useState(false)
  const [patientList, setPatientList] = useState([] as any)
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deletePatient, setDeletePatient] = useState({} as any)
  const [currResetPatient, setCurrResetPatient] = useState({} as any)
  const [testStatus, setTestStatus] = useState(null)
  const [clinicalTeam, setClinicalTeam] = useState(null) as any
  const dispatch = useAppThunkDispatch()
  const [patientTotal, setPatientTotal] = useState({
    total: '',
    showing: '',
  }) as any

  const metaData = useSelector((state: RootState) => state.metaData.metadata)

  const [elationStatus, setElationStatus] = useState(null as any)
  const [tableData, setTableData] = useState(() => {
    const baseTableData = tableHeadeClinicVisit

    // Apply saved sort state if it exists
    if (clinicVisitsTable.sort) {
      const sortPairs = clinicVisitsTable.sort
        .split(',')
        .filter((s: any) => s.trim())
        .map((sortStr: any) => {
          const parts = sortStr.split('-')
          return {
            column: parts[0],
            order: parts[1] || 'none',
          }
        })

      const sortMap = new Map(
        sortPairs.map((pair: any) => [pair.column, pair.order])
      )

      return baseTableData.map((el: any) => ({
        ...el,
        sort: sortMap.get(el.sortColumn) || 'none',
      }))
    }

    return baseTableData
  })
  const [openAddPatient, setOpenAddPatient] = useState(false)
  const [openExtract, setOpenExtract] = useState(false)
  const handleAddPatient = () => setOpenAddPatient(true)
  const handleExtract = () => setOpenExtract(true)
  const handleCloseExtract = () => setOpenExtract(false)
  const CARDIOLOGISTS = useSelector(
    (state: RootState) => state.metaData.metadata?.CARDIOLOGISTS
  )

  const handleAddPatientClose = (res: boolean = false) => {
    if (res) {
      setLoading(true)
      setupData()
    }
    setOpenAddPatient(false)
  }

  const handleMeasurementSync = () => {
    setLoading(true)
    patientService
      .syncDeviceMeasurements()
      .then((_res: any) => {
        setLoading(false)
        toast.success(
          'Device measurement sync successful.',
          Helper.bottom_center()
        )
        getPatientList()
      })
      .catch((error) => {
        setLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }

  const getFilterStateKey = useCallback(() => {
    const { scrollPosition, preserveData, isSubmit, ...filterParams } =
      clinicVisitsTable
    return JSON.stringify(filterParams)
  }, [clinicVisitsTable])

  useEffect(() => {
    const interval = setInterval(async () => {
      setElationStatus(localStorage.getItem('importStatus'))
      if (
        localStorage.getItem('importStatus') === 'success' ||
        localStorage.getItem('importStatus') === 'ongoing'
      ) {
        getImportStatus()
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const hasPreservedData = false
    //   patientList?.length > 0 && clinicVisitsTable?.preserveData === 'true'
    const currentFilterKey = getFilterStateKey()
    const filtersChanged = lastFetchParams !== currentFilterKey

    // Key Fix: Check shouldRestoreState to prevent API call on navigation
    const shouldFetchData =
      !hasPreservedData &&
      (clinicVisitsTable?.isSubmit === 'submit' || isPagination) &&
      !loading &&
      clinicVisitsTable?.shouldRestoreState !== 'true' // Check for STRING 'true'

    if (shouldFetchData && filtersChanged) {
      getData()
      setLastFetchParams(currentFilterKey)
      setDataInitialized(true)
    }
    // else if (
    //   hasPreservedData &&
    //   clinicVisitsTable?.shouldRestoreState === 'true'
    // ) {
    //   // Restore mode: Data exists and we're returning from detail page
    //   setDataInitialized(true)
    //   // Clear the restore flag after a short delay
    //   setTimeout(() => {
    //     dispatch(
    //       setClinicVisitsTable({
    //         ...clinicVisitsTable,
    //         preserveData: '',
    //         isSubmit: '',
    //         shouldRestoreState: 'false', //  Reset to STRING 'false'
    //       })
    //     )
    //   }, 100)
    // }
    //  else if (hasPreservedData && !filtersChanged) {
    //   // Fallback: Preserve existing data if filters haven't changed
    //   setDataInitialized(true)
    //   setTimeout(() => {
    //     dispatch(
    //       setClinicVisitsTable({
    //         ...clinicVisitsTable,
    //         preserveData: '',
    //         isSubmit: '',
    //         shouldRestoreState: 'false', // Reset to STRING 'false'
    //       })
    //     )
    //   }, 100)
    // }
  }, [
    clinicVisitsTable?.isSubmit,
    isPagination,
    getFilterStateKey,
  ])

  const getData = () => {
    setupData()

    // Parse the sort string properly
    if (clinicVisitsTable.sort) {
      const sortPairs = clinicVisitsTable.sort
        .split(',')
        .filter((s: any) => s.trim())
        .map((sortStr: any) => {
          const parts = sortStr.split('-')
          return {
            column: parts[0],
            order: parts[1] || 'none',
          }
        })

      const sortMap = new Map(
        sortPairs.map((pair: any) => [pair.column, pair.order])
      )

      setTableData((prevTableData: any) =>
        prevTableData.map((el: any) => ({
          ...el,
          sort: sortMap.get(el.sortColumn) || 'none',
        }))
      )
    } else {
      // Reset all sorts to 'none' if no sort in Redux
      setTableData((prevTableData: any) =>
        prevTableData.map((el: any) => ({
          ...el,
          sort: 'none',
        }))
      )
    }
  }

  const setupData = () => {
    try {
      if (loading) return
      getPatientList()
      generateChips()
    } catch (error) {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    }
  }

  useEffect(() => {
    const temp = tableData
      .filter((f: any) => f.sort !== 'none')
      .map((e: any) => e.sortColumn + '-' + e.sort)
      .join(',')
    if (temp) {
      dispatch(
        setClinicVisitsTable({
          ...clinicVisitsTable,
          page: 1,
          sort: temp,
          isSubmit: 'submit',
        })
      )
    }
  }, [tableData])

  useEffect(() => {
    if (
      patientList.length >= 50 &&
      tableEl?.current?.scrollHeight === tableEl?.current?.clientHeight
    ) {
      dispatch(
        setClinicVisitsTable({
          ...clinicVisitsTable,
          limit: clinicVisitsTable.limit + 30,
        })
      )
    }
  }, [patientList])

  const getElationImport = () => {
    patientService
      .getElationImport()
      .then((res: any) => {
        localStorage.setItem('importStatus', res?.data?.message)
        setElationStatus(res?.data?.message)
        if (res?.data?.message === 'success')
          toast.success('Elation import started', Helper.bottom_center())
      })
      .catch((error) =>
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      )
  }

  const getImportStatus = async () => {
    patientService
      .elationimportStatus()
      .then((res: any) => {
        localStorage.setItem('importStatus', res?.data?.message)
        setElationStatus(res?.data?.message)
      })
      .catch((error) =>
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      )
  }

  const handleImportReport = () => {
    patientService
      .downloadeElationReport()
      .then((res: any) => {
        if (res.data) {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(new Blob([res.data]))
          link.setAttribute('download', `elation_import_status.csv`)
          document.body.appendChild(link)
          link.click()
        }
      })
      .catch((error: any) =>
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      )
  }

  const sortIcon = (d: any) => {
    if (!d) return null
    if (d.sort === 'none') {
      return <UnfoldMoreIcon className={classes.fontSizeSmall} />
    } else if (d.sort === 'desc') {
      return <ArrowDropDownIcon className={classes.fontSizeSmall} />
    } else if (d.sort === 'asc') {
      return <ArrowDropUpIcon className={classes.fontSizeSmall} />
    }
  }

  const handleSort = (d: any) => {
    let nextSort = ''
    if (d.sort === 'none') {
      nextSort = 'asc'
    } else if (d.sort === 'asc') {
      nextSort = 'desc'
    } else if (d.sort === 'desc') {
      nextSort = 'none'
    }
    setTableData(
      tableData.map((el: any) =>
        el.header === d.header ? { ...el, sort: nextSort } : el
      )
    )
  }

  const getPatientList = () => {
    let patientFilterList = { ...clinicVisitsTable }
    // Remove flags that shouldn't be sent to API
    delete patientFilterList['isSubmit']
    delete patientFilterList['scrollPosition']
    delete patientFilterList['preserveData']

    setLoading(true)
    isApply && SetIsApply(false)
    const isClinicVisit = url.pathname === '/clinic-visits' || Helper.isAIDA()

    patientService
      .getPatientsWithFilters(patientFilterList, isClinicVisit)
      .then(async (response: any) => {
        let currData = []

        if (clinicVisitsTable.page > 1) {
          // Pagination - append data
          if (response?.data?.patients_program_merged) {
            currData = [
              ...patientList,
              ...response?.data?.patients_program_merged,
            ]
          }
        } else {
          // New search/filter - replace data
          tableEl?.current?.scrollTo(0, 0)
          currData = response?.data?.patients_program_merged || []
        }

        setLoading(false)
        setIsPagination(false)

        setPatientList(currData)
        setPatientTotal({
          total: response?.data?.patients_count,
          showing: currData.length,
        })
      })
      .catch((error: any) => {
        setError(error)
        setLoading(false)
        setIsPagination(false)
      })
  }

  const setError = (error: any) => {
    toast.error(Helper.getErrorText(error), {
      position: 'bottom-center',
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
    })
  }

  const generateChips = () => {
    const {
      page,
      limit,
      sort,
      ...chipsd
    }: { page: number; limit: number; sort: string; chipsd: string[] } = {
      ...clinicVisitsTable,
    }
    const getDisplayName = (key: any, currData: any) => {
      return ['stage', 'status'].includes(key)
        ? currData
          ?.split(',')
          ?.map((i: any) => helper.stageName(i))
          ?.join(', ')
        : currData
    }

    setProgramChips(
      Object.entries(chipsd)
        ?.filter((f: any) => f[1] !== '')
        ?.map((m: any) => {
          return {
            key: m[0],
            value: m[1],
            display_name: getDisplayName(m[0], m[1]),
          }
        })
    )
  }

  const getPrgDisplayName = (a: any) => {
    const currPrg =
      programChips?.find((item: any) => item['key'] === 'selectedPrg')?.value ||
      []
    return currPrg.find((i: any) => i.program === a)?.display_name || a
  }

  const handleDeleteByType = (
    key: string,
    isProgram = false,
    chipType?: string
  ) => {
    let newValue = ''
    const currentValue =
      programChips?.find((item: any) => item['key'] === 'clinic_visit_status')
        ?.value || ''
    if (currentValue) {
      const statusValues = currentValue.split(',').map((v: string) => v.trim())

      if (chipType === 'overdue') {
        // Remove only overdue values
        const nonOverdueValues = statusValues.filter(
          (v: string) => !v.toUpperCase().includes('OVERDUE')
        )
        newValue = nonOverdueValues.join(',')
      } else {
        // Remove only non-overdue values
        const overdueValues = statusValues.filter((v: string) =>
          v.toUpperCase().includes('OVERDUE')
        )
        newValue = overdueValues.join(',')
      }
      updatePatientObject(key, newValue)
    }
  }

  const handleDelete = (key: string, isProgram = false) => {
    updatePatientObject(
      key,
      ['approval_status', 'inactive'].includes(key) ? 'false' : ''
    )
  }

  const updatePatientObject = (key: string, value: any) => {
    dispatch(
      setClinicVisitsTable({
        ...clinicVisitsTable,
        isSubmit: 'submit',
        page: 1,
        limit: 50,
        [key]: value,
        shouldRestoreState: 'false', //  Use STRING 'false' - trigger API call
        scrollPosition: 0, //  Reset scroll on new filter
        preserveData: '', //  Clear preservation on new filter
      })
    )

    if (key === 'stage') dispatch(setSelectedStage(''))
  }

  const fetchNextSetData = () => {
    if (
      !loading &&
      patientTotal.showing !== patientTotal.total &&
      !isPagination
    ) {
      setIsPagination(true)
      dispatch(
        setClinicVisitsTable({
          ...clinicVisitsTable,
          page: clinicVisitsTable.page + 1,
        })
      )
    }
  }

  const scrollListener = useCallback(() => {
    const tableElement = tableEl?.current
    if (!tableElement) return

    const scrollTop = tableElement.scrollTop
    const scrollHeight = tableElement.scrollHeight
    const clientHeight = tableElement.clientHeight
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const triggerDistance = Math.round(clientHeight * 0.2)

    if (
      !loading &&
      !isPagination &&
      distanceFromBottom < triggerDistance &&
      patientTotal.showing < patientTotal.total
    ) {
      fetchNextSetData()
    }
  }, [loading, patientTotal, fetchNextSetData])

  useLayoutEffect(() => {
    const tableRef = tableEl?.current
    tableRef?.addEventListener('scroll', scrollListener)
    return () => {
      tableRef?.removeEventListener('scroll', scrollListener)
    }
  }, [scrollListener])

  const confirmResetStatus = () => {
    currResetPatient?.patient_id &&
      patientService
        .resetPatient(currResetPatient?.patient_id, {
          programs: currResetPatient?.program_name,
        })
        .then((_res: any) => {
          setConfirmResetOpen(false)
          setCurrResetPatient({})
          toast.success(
            `Patient ${currResetPatient.first_name} reset successfully`,
            Helper.bottom_center()
          )
        })
        .catch((error) => {
          setConfirmResetOpen(false)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
  }

  const handleConfirmReset = () => setConfirmResetOpen(false)

  const onExport = (patientId: any) => {
    patientService
      .exportPatient(patientId)
      .then((res: any) => {
        const exportUrl = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = exportUrl
        link.setAttribute('download', `${patientId}.json`)
        document.body.appendChild(link)
        link.click()
      })
      .catch((error) =>
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      )
  }

  const handleConfirmDelete = () => {
    setDeletePatient({})
    setConfirmDelete(false)
  }

  const handleDeletePatient = (patientInfo: any) => {
    setDeletePatient(patientInfo)
    setConfirmDelete(true)
  }

  const confirmDeletePatient = () => {
    deletePatient?.patient_id !== '' &&
      patientService
        .deletePatient(deletePatient?.patient_id)
        .then((res: any) => {
          setConfirmDelete(false)
          toast.success(
            `Patient ${deletePatient?.patient_name} deleted successfully.`,
            Helper.bottom_center()
          )
          window.location.reload()
        })
        .catch((error) => {
          setConfirmDelete(false)
          toast.error(Helper.getErrorText(error), Helper.bottom_center())
        })
        .finally(() => setDeletePatient(''))
  }

  const getPatientActions = (row: any) => {
    return (
      <Stack direction="row" spacing={0.1} alignItems={'center'}>
        <Tooltip title="Export patient" placement="bottom">
          <IconButton
            aria-label="Export patient"
            color="inherit"
            onClick={() => onExport(row.patient_id)}
            sx={{ fontSize: '1rem', padding: '2px 0px 0px 0px' }}
          >
            <GetAppIcon className={classes.resetPatientIcon} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset patient" placement="bottom">
          <IconButton
            aria-label="Reset Patient"
            disabled={!row.is_test_patient}
            color="inherit"
            size="small"
            className={classes.zeroPadding}
            onClick={() => {
              setConfirmResetOpen(true)
              setCurrResetPatient({
                patient_id: row.patient_id,
                first_name: row.patient_name,
                program_name: row?.program_name,
              })
            }}
          >
            <RefreshIcon
              className={classes.resetPatientIcon}
              sx={{ color: !row.is_test_patient ? 'gray !important' : '' }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete patient" placement="bottom">
          <IconButton
            aria-label="Delete patient"
            color="inherit"
            onClick={() =>
              handleDeletePatient({
                patient_id: row.patient_id,
                patient_name: row.patient_name,
              })
            }
            className={classes.zeroPadding}
            size="small"
          >
            <DeleteOutlinedIcon className={classes.resetPatientIcon} />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }

  const getReferralStatus = (row: any) => {
    return checkCondition(
      row.referral_status,
      checkCondition(
        row.referral_status == 'ESCALATED',
        <IconButton className={classes.iconBaseBorder}>
          <PriorityHighIcon
            className={[classes.notVal2, classes.compactMenuIcon].join(' ')}
          />
        </IconButton>,
        checkCondition(
          row.referral_status == 'RESOLVED',
          <IconButton className={classes.iconBaseBorder}>
            <CheckCircleOutlineIcon
              className={[classes.notVal, classes.compactMenuIcon].join(' ')}
            />
          </IconButton>,
          checkCondition(
            row.referral_status == 'OPEN',
            <IconButton className={classes.iconBaseBorder}>
              <PriorityHighIcon
                className={[classes.notVal, classes.compactMenuIcon].join(' ')}
              />
            </IconButton>,
            ''
          )
        )
      )
    )
  }

  const getDate = (date: any) => {
    return date ? moment.unix(date).format('MMM DD YYYY hh:mm a') : ''
  }

  const getVisitStatus = (statusString = '') => {
    if (!statusString) return ''
    return statusString
      ?.split(',')
      ?.map(
        (status) =>
          status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
      )
      ?.join(', ')
  }

  const getCardiologist = (val: any) => {
    const ids = val.split(',').map((id: any) => parseInt(id.trim()))
    return (
      ids
        ?.map(
          (id: any) =>
            CARDIOLOGISTS?.find((d: any) => d.id === id)?.name || null
        )
        ?.filter((name: any) => name !== null) || ''
    )
  }

  const getFilterList = () => {
    const filterObj = programChips?.map((c: any, j: any) => {
      if (c.key === 'isSubmit') return null

      const chipKey = `prg-chip-${j}`
      if (c.key === 'programs' || c.key === 'selectedPrg') {
        if (c.key === 'programs' && c.value) {
          return (
            <React.Fragment key={chipKey}>
              {c.value.split(',').map((programId: string) => (
                <Chip
                  key={`${j}-${programId}`}
                  className={classes.chipSearch}
                  label={<small>{getPrgDisplayName(programId)}</small>}
                  variant="outlined"
                  onDelete={() => handleDelete(programId, true)}
                />
              ))}
            </React.Fragment>
          )
        }
        return null
      }

      const value = c.display_name || c.value
      let chipLabel = null
      let shouldRender = false
      switch (c.key) {
        case 'approval_status':
          if (value === 'true' || value === true) {
            chipLabel = 'Approval needed'
            shouldRender = true
          }
          break
        case 'cardiologist':
          if (value) {
            chipLabel = `Cardiologist ${getCardiologist(value)}`
            shouldRender = true
          }
          break
        case 'activity_days':
          if (value > 0) {
            chipLabel = `No activity since ${value} day${value > 1 ? 's' : ''}`
            shouldRender = true
          }
          break
        case 'clinic_visit_status':
          if (value) {
            // Handle multiple values separated by comma
            const statusValues = value.split(',').map((v: string) => v.trim())
            const overdueValues = statusValues.filter((v: string) =>
              v.toUpperCase().includes('OVERDUE')
            )
            const otherValues = statusValues.filter(
              (v: string) => !v.toUpperCase().includes('OVERDUE')
            )

            const chips = []
            // Create chip for overdue values
            if (overdueValues.length > 0) {
              chips.push(
                <Chip
                  key={`${j}-overdue`}
                  className={classes.chipSearch}
                  label={<small>Overdue</small>}
                  variant="outlined"
                  onDelete={() => handleDeleteByType(c.key, false, 'overdue')}
                />
              )
            }

            // Create chip for other values
            if (otherValues.length > 0) {
              chips.push(
                <Chip
                  key={`${j}-other`}
                  className={classes.chipSearch}
                  label={
                    <small>{`Visit status ${getVisitStatus(
                      otherValues.join(', ')
                    )}`}</small>
                  }
                  variant="outlined"
                  onDelete={() => handleDeleteByType(c.key, false, 'other')}
                />
              )
            }

            return chips.length > 0 ? (
              <React.Fragment key={chipKey}>{chips}</React.Fragment>
            ) : null
          }
          break
        case 'clinic_visit_days':
          if (value) {
            chipLabel = `Visit due ${getVisitStatus(value)} days`
            shouldRender = true
          }
          break
        case 'inactive':
          if (value > 0) {
            chipLabel = 'Inactive patients'
            shouldRender = true
          }
          break
        case 'scrollPosition':
          shouldRender = false
          break
        case 'shouldRestoreState':
          shouldRender = false
          break
        case 'preserveData':
          shouldRender = false
          break
        default:
          chipLabel = value
          shouldRender = true
          break
      }

      return shouldRender ? (
        <React.Fragment key={chipKey}>
          <Chip
            key={j}
            className={classes.chipSearch}
            label={<small>{chipLabel}</small>}
            variant="outlined"
            onDelete={() => handleDelete(c.key, false)}
          />
        </React.Fragment>
      ) : null
    })
    return <Box>{filterObj}</Box>
  }

  const handleOpenPatientDetails = (patientId: string) => {
    history.push(`/patients/${patientId}/details`)
  }

  return (
    <>
      {loading && <LinearProgress />}
      <Box p={'0.5rem 0rem 0.5rem 0.2rem'}>
        {elationStatus === 'ongoing' && (
          <Typography variant="subtitle2" pl={1}>
            {`Elation import in progress...`}&nbsp;
            <CircularProgress size={15} />
          </Typography>
        )}
        <Stack spacing={0.5} pb={0.8} pr={1} pt={1}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems={'center'}
            flexWrap={'wrap'}
          >
            <Grid item xs={8}>
              <Typography
                className={classes.desc}
                variant="body2"
                pl={1}
                pb={0.5}
              >
                {Number(patientTotal?.showing) < 1 &&
                  clinicVisitsTable.isSubmit === '' && (
                    <Typography
                      className={classes.desc}
                      variant="subtitle2"
                      pl={1}
                    >
                      {`To get patient list, you'll need to click the "Apply" button`}
                    </Typography>
                  )}
                {loading ? (
                  <CircularProgress
                    size={10}
                    sx={{ margin: '6px 0px 0px 6px' }}
                  />
                ) : Number(patientTotal?.showing) > 0 ? (
                  `Showing ${patientTotal.showing} out of ${patientTotal.total
                  } patient${Number(patientTotal.total) !== 1 ? 's' : ''}`
                ) : (
                  ''
                )}
              </Typography>
            </Grid>
            <Grid item justifyItems={'flex-end'} justifyContent={'center'}>
              <Stack direction="row" spacing={0.5} alignItems={'baseline'}>
                <Tooltip title="Add patient" placement="bottom">
                  <IconButton
                    size="small"
                    disabled={loading}
                    onClick={handleAddPatient}
                    className={classes.zeroPadding}
                  >
                    <PersonAddAlt1Icon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sync device measurements" placement="bottom">
                  <IconButton
                    disabled={loading}
                    onClick={handleMeasurementSync}
                    className={classes.zeroPadding}
                  >
                    <SyncIcon sx={{ fontSize: '0.95rem' }} />
                  </IconButton>
                </Tooltip>
                {environment.okta.isEnabled && !Helper.is_AIDA_OIDC() && (
                    <Tooltip title="Elation import" placement="bottom">
                      <IconButton
                        size="small"
                        disabled={
                          loading ||
                          elationStatus === 'null' ||
                          elationStatus === 'ongoing'
                        }
                        className={classes.zeroPadding}
                        onClick={getElationImport}
                      >
                        <GroupAddIcon
                          sx={{
                            fontSize: '1rem',
                            fill:
                              !loading && elationStatus === 'completed'
                                ? 'green'
                                : 'grey',
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                )}
                {environment.okta.isEnabled && !Helper.is_AIDA_OIDC() && (
                  <Tooltip title="Elation import status" placement="bottom">
                    <IconButton
                      size="small"
                      className={classes.zeroPadding}
                      disabled={loading || elationStatus !== 'completed'}
                      onClick={handleImportReport}
                    >
                      <DescriptionIcon
                        sx={{
                          fontSize: '0.95rem',
                          fill:
                            !loading && elationStatus === 'completed'
                              ? 'green'
                              : 'grey',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {openAddPatient && (
                  <AddPatientComponent
                    openAddPatient={openAddPatient}
                    onAddPatientClose={(res: boolean = false) =>
                      handleAddPatientClose(res)
                    }
                  />
                )}
                {openExtract && (
                  <ExtractPatientComponent
                    openExtract={openExtract}
                    handleCloseExtract={handleCloseExtract}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
          {/* get filter chips */}
          {getFilterList()}
        </Stack>
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            maxHeight: `${window.innerHeight / 20}rem !important`,
            overflowX: 'auto',
          }}
          ref={tableEl}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {tableData.map((t: any, i: number) => (
                  <TableCell
                    key={`patient-${i}`}
                    className={`${classes.patientListHeadersBorder} ${classes.taskTableHeader}`}
                    sx={{
                      minWidth: t.width || '5rem !important',
                      maxWidth: [
                        'Patient',
                        'MRN',
                        'Clinic Patient ID',
                      ].includes(t.header)
                        ? 'max-content !important'
                        : '',
                    }}
                    color="textSecondary"
                    align={t.align}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      sx={{ width: 'max-content !important' }}
                    >
                      <Typography className={classes.taskTableHeaderTitle}>
                        {t.header}
                      </Typography>
                      {(Helper.isAIDA() &&
                        [
                          'mrn',
                          'date_of_birth',
                          'patient_id',
                          'is_inactive',
                        ].includes(t.sortColumn)) ||
                        (!Helper.isAIDA() &&
                          ![
                            'stage',
                            'test_status',
                            'patient_name',
                            'action',
                            'referral_status',
                            'referral_last_modified_on',
                            'sbp',
                            'dbp',
                            'last_recorded_date',
                          ].includes(t.sortColumn) && (
                            <IconButton
                              sx={{ padding: '1px 0px !important' }}
                              onClick={() => handleSort(t)}
                            >
                              {sortIcon(t)}
                            </IconButton>
                          ))}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody id="scrollableDivPL">
              {patientList?.map((row: any, i: number) => (
                <TableRow
                  key={`pl-demo-${i}`}
                  sx={{
                    padding: '0.1rem 0.1rem 0.1rem 0.3rem !important',
                    backgroundColor: [
                      testStatus,
                      clinicalTeam?.patient_id,
                    ].includes(row.patient_id)
                      ? 'rgba(46, 125, 50, 0.15)'
                      : confirmDelete &&
                        deletePatient?.patient_id === row.patient_id
                        ? 'rgba(238, 24, 4, 0.11)'
                        : confirmResetOpen &&
                          currResetPatient?.patient_id === row.patient_id
                          ? 'var(--color-background-subtle)'
                          : row.color === 'W'
                            ? 'var(--color-paper)'
                            : 'var(--color-background-subtle)',
                  }}
                >
                  {tableData.map((t: any, i: number) => (
                    <>
                      {t.sortColumn === 'patient_name' ? (
                        <TableCell
                          className={classes.tablePaddingPList}
                          sx={{
                            minWidth: t.width,
                            maxWidth: 'max-content !important',
                          }}
                        >
                          <Typography
                            variant="body2"
                            onClick={() =>
                              handleOpenPatientDetails(row.patient_id)
                            }
                            className={`${classes.patientsName} ${classes.desc}`}
                            sx={{ cursor: 'pointer' }}
                          >
                            {`${row.patient_name}`}
                            <ColorBubbleComponent
                              selectedProgramList={row?.program_name}
                            />
                          </Typography>
                        </TableCell>
                      ) : t.sortColumn === 'mrn' ? (
                        <TableCell
                          className={[
                            classes.tablePaddingPList,
                            classes.overflowWrapAnywhere,
                          ].join(' ')}
                          align="left"
                        >
                          <Typography className={classes.desc} variant="body2">
                            {checkCondition(
                              row.mrn !== null,
                              row.mrn?.join(', '),
                              ''
                            )}
                          </Typography>
                        </TableCell>
                      ) : t.sortColumn === 'program_name' ? (
                        <TableCell
                          className={[
                            classes.tablePaddingPList,
                            classes.overflowWrapAnywhere,
                          ].join(' ')}
                        >
                          <Typography className={classes.desc} variant="body2">
                            {row.program_display_name.join(', ')}
                          </Typography>
                        </TableCell>
                      ) : t.sortColumn === 'date_of_birth' ? (
                        <TableCell
                          className={[
                            classes.tablePaddingPList,
                            classes.overflowWrapAnywhere,
                          ].join(' ')}
                        >
                          <Typography className={classes.desc} variant="body2">
                            {row[t.sortColumn]
                              ? moment(row[t.sortColumn]).format('MMM DD YYYY')
                              : ''}
                          </Typography>
                        </TableCell>
                      ) : t.sortColumn === 'clinic_visit_status' ? (
                        <TableCell
                          className={[
                            classes.tablePaddingPList,
                            classes.overflowWrapAnywhere,
                          ].join(' ')}
                          align="left"
                        >
                          <Typography className={classes.desc} variant="body2">
                            {row.clinic_visit_status
                              ? helper.toCamelCase(
                                row.clinic_visit_status.toLowerCase()
                              )
                              : ''}
                          </Typography>
                        </TableCell>
                      ) : (Helper.isAIDA() && t.sortColumn) ===
                        'clinic_visit_date' ? (
                        <TableCell
                          className={[
                            classes.tablePaddingPList,
                            classes.overflowWrapAnywhere,
                          ].join(' ')}
                        >
                          <Typography
                            className={classes.desc}
                            variant="body2"
                            color={
                              row?.clinic_visit_overdue ? 'red' : 'inherit'
                            }
                          >
                            {row['clinic_visit_date']
                              ? moment
                                .unix(row['clinic_visit_date'])
                                .format('MMM DD YYYY')
                              : ''}
                          </Typography>
                        </TableCell>
                      ) : t.sortColumn === 'is_inactive' ? (
                        <TableCell className={classes.tablePaddingPList}>
                          {row?.is_inactive && (
                            <PersonRemoveIcon
                              titleAccess={'Patient inactive'}
                              sx={{
                                fontSize: '15px',
                                fill: 'grey',
                                paddingLeft: '1rem',
                              }}
                            />
                          )}
                        </TableCell>
                      ) : // : t.sortColumn === 'clinical_team'
                        //   ? <TableCell className={classes.tablePaddingPList} align='center'>
                        //     <IconButton aria-label='Clinical team'
                        //       onMouseOver={() => {
                        //         setTestStatus(null);
                        //         setClinicalTeam({
                        //           ...row.clinical_team,
                        //           patient_id: row.patient_id
                        //         })
                        //       }}
                        //       sx={{ fontSize: '1rem' }}
                        //     >
                        //       <InfoIcon sx={{ fill: 'grey', position: 'relative' }} className={classes.resetPatientIcon} />
                        //     </IconButton>
                        //   </TableCell>
                        t.sortColumn === 'referral_status' ? (
                          <TableCell
                            className={[
                              classes.tablePaddingPList,
                              classes.alignTextCenter,
                            ].join(' ')}
                            sx={{ textAlign: t.align }}
                          >
                            {getReferralStatus(row)}
                          </TableCell>
                        ) : t.sortColumn === 'referral_last_modified_on' ? (
                          <TableCell
                            className={[
                              classes.tablePaddingPList,
                              classes.overflowWrapAnywhere,
                            ].join(' ')}
                          >
                            <Typography className={classes.desc} variant="body2">
                              {row.referral_status &&
                                row.referral_status != 'NOT_AVAILABLE'
                                ? row.referral_last_modified_on
                                  ? moment
                                    .unix(row.referral_last_modified_on)
                                    .format('MMM DD YYYY hh:mm a')
                                  : ''
                                : ''}
                            </Typography>
                          </TableCell>
                        ) : t.sortColumn === 'test_status' ? (
                          <TableCell
                            className={classes.tablePaddingPList}
                            align="center"
                            sx={{ lineHeight: 1 }}
                          >
                            <IconButton
                              aria-label="Test status"
                              size="small"
                              onMouseOver={() => {
                                setClinicalTeam(null)
                                setTestStatus(row.patient_id)
                              }}
                              className={classes.zeroPadding}
                            >
                              <InfoIcon
                                sx={{ fill: 'grey', position: 'relative' }}
                                className={classes.resetPatientIcon}
                              />
                            </IconButton>
                          </TableCell>
                        ) : t.sortColumn === 'action' ? (
                          <TableCell
                            className={classes.tablePaddingPList}
                            align="left"
                          >
                            {getPatientActions(row)}
                          </TableCell>
                        ) : (
                          <TableCell
                            className={classes.tablePaddingPList}
                            align="left"
                            sx={{
                              // minWidth: t.width,
                              width: 'max-content !important',
                            }}
                          >
                            <Typography
                              className={classes.desc}
                              variant="body2"
                              sx={{
                                textAlign: `${t.align} !important`,
                                minWidth: t.width,
                                width: 'max-content !important',
                              }}
                            >
                              {['sbp', 'dbp'].includes(t.sortColumn)
                                ? t.sortColumn === 'sbp'
                                  ? row.sbp?.value || ''
                                  : row.dbp?.value || ''
                                : [
                                  'last_recorded_date',
                                  'mri_order_date',
                                  'mri_result_arrival_date',
                                  'echo_order_date',
                                  'echo_result_arrival_date',
                                  'pyp_order_date',
                                  'pyp_result_arrival_date',
                                ].includes(t.sortColumn)
                                  ? getDate(row[t.sortColumn])
                                  : ['stage'].includes(t.sortColumn)
                                    ? Helper.toCamelCaseFirstWord(row[t.sortColumn])
                                    : ['al_labs_completed'].includes(t.sortColumn)
                                      ? row[t.sortColumn] !== null
                                        ? Helper.toCamelCase(
                                          JSON.stringify(row[t.sortColumn])
                                        )
                                        : ''
                                      : row[t.sortColumn]}
                            </Typography>
                          </TableCell>
                        )}
                    </>
                  ))}
                </TableRow>
              ))}
              {!loading && patientList.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={9}>
                    <Typography className={classes.desc} variant="body2">
                      <strong>{isApply ? `` : `Data not available!`}</strong>
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableHead>
              {loading && (
                <TableRow>
                  <TableCell align="center" colSpan={9}>
                    <Typography className={classes.desc} variant="body2">
                      <strong>Loading...</strong>
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableHead>
          </Table>
        </TableContainer>
        {clinicalTeam && (
          <TestStatusDialogComponent
            open={true}
            input={{
              title: 'Clinical team',
              data: clinicalTeam,
            }}
            handleConfirmClose={() => setClinicalTeam(null)}
          />
        )}
        {testStatus && (
          <TestStatusDialogComponent
            open={true}
            input={{
              title: 'Test status',
              refId: testStatus,
            }}
            handleConfirmClose={() => setTestStatus(null)}
          />
        )}
        {confirmResetOpen && (
          <ConfirmationDialogComponent
            open={confirmResetOpen}
            content={`Are you sure you want to reset the patient ${currResetPatient?.first_name || ''
              } details?`}
            handleConfirmClose={handleConfirmReset}
            confirmStatus={confirmResetStatus}
          />
        )}
        {confirmDelete && (
          <ConfirmationDialogComponent
            open={confirmDelete}
            content={`Are you sure you want to delete the patient ${deletePatient?.patient_name || ''
              }?`}
            handleConfirmClose={handleConfirmDelete}
            confirmStatus={confirmDeletePatient}
          />
        )}
      </Box>
    </>
  )
}

export default memo(ClinicVisitTableComponent)
